export const EVENTS = Object.freeze({
  PAGE_VIEW: "page_view",
  CTA_CLICK: "cta_click",
  VIEW_CONFIGURATOR: "view_configurator",
  SELECT_PRODUCT: "select_product",
  START_CONFIGURATION: "start_configuration",
  UPDATE_CONFIGURATION: "update_configuration",
  PREVIEW_GENERATED: "preview_generated",
  CONFIGURATOR_HELP: "configurator_help",
  START_CHECKOUT: "start_checkout",
  CHECKOUT_REDIRECT: "checkout_redirect",
  CHECKOUT_ERROR: "checkout_error",
  PURCHASE_COMPLETED: "purchase_completed",
  NEWSLETTER_SIGNUP: "newsletter_signup",
  CONTACT_SUBMIT: "contact_submit",
})

export const KNOWN_EVENT_NAMES = Object.freeze(Object.values(EVENTS))

const TRUE_VALUES = new Set(["1", "true", "yes", "on"])
const DEFAULT_PLAUSIBLE_SCRIPT_URL = "https://plausible.io/js/script.js"
const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/i
const GA4_ID_PATTERN = /^G-[A-Z0-9]+$/i

const BLOCKED_PROPERTY_PATTERNS = [
  /address/i,
  /customer/i,
  /email/i,
  /first.?name/i,
  /last.?name/i,
  /^name$/i,
  /payment/i,
  /phone/i,
  /preview/i,
  /session/i,
  /stripe/i,
]

const normalizeString = value => (typeof value === "string" ? value.trim() : "")

const parseBoolean = value =>
  TRUE_VALUES.has(normalizeString(value).toLowerCase())

const normalizeUrl = value => {
  const url = normalizeString(value) || DEFAULT_PLAUSIBLE_SCRIPT_URL

  try {
    const parsed = new URL(url)
    return ["http:", "https:"].includes(parsed.protocol)
      ? parsed.toString()
      : DEFAULT_PLAUSIBLE_SCRIPT_URL
  } catch {
    return DEFAULT_PLAUSIBLE_SCRIPT_URL
  }
}

const normalizeGtmId = value => {
  const id = normalizeString(value).toUpperCase()
  return GTM_ID_PATTERN.test(id) ? id : ""
}

const normalizeGa4MeasurementId = value => {
  const id = normalizeString(value).toUpperCase()
  return GA4_ID_PATTERN.test(id) ? id : ""
}

export const getAnalyticsConfig = (env = process.env) => {
  const plausibleDomain = normalizeString(env.GATSBY_PLAUSIBLE_DOMAIN)
  const plausibleEnabled =
    parseBoolean(env.GATSBY_PLAUSIBLE_ENABLED) && Boolean(plausibleDomain)

  const marketingTagsRequested = parseBoolean(env.GATSBY_MARKETING_TAGS_ENABLED)
  const gtmId = marketingTagsRequested ? normalizeGtmId(env.GATSBY_GTM_ID) : ""
  const ga4MeasurementId = marketingTagsRequested
    ? normalizeGa4MeasurementId(env.GATSBY_GA4_MEASUREMENT_ID)
    : ""
  const hasMarketingProvider = Boolean(gtmId || ga4MeasurementId)

  return Object.freeze({
    plausible: Object.freeze({
      enabled: plausibleEnabled,
      domain: plausibleEnabled ? plausibleDomain : "",
      scriptUrl: normalizeUrl(env.GATSBY_PLAUSIBLE_SCRIPT_URL),
    }),
    marketing: Object.freeze({
      requested: marketingTagsRequested,
      showConsentBanner: marketingTagsRequested,
      canLoadTags: marketingTagsRequested && hasMarketingProvider,
      hasProvider: hasMarketingProvider,
      gtmId,
      ga4MeasurementId,
    }),
  })
}

export const analyticsConfig = getAnalyticsConfig()

export const isKnownEventName = eventName =>
  KNOWN_EVENT_NAMES.includes(eventName)

const isBlockedProperty = key =>
  BLOCKED_PROPERTY_PATTERNS.some(pattern => pattern.test(key))

export const sanitizeEventProps = props => {
  if (!props || typeof props !== "object" || Array.isArray(props)) {
    return {}
  }

  return Object.entries(props).reduce((safeProps, [key, value]) => {
    if (!key || isBlockedProperty(key)) {
      return safeProps
    }

    if (typeof value === "string") {
      safeProps[key] = value.slice(0, 120)
      return safeProps
    }

    if (typeof value === "number" && Number.isFinite(value)) {
      safeProps[key] = value
      return safeProps
    }

    if (typeof value === "boolean") {
      safeProps[key] = value
    }

    return safeProps
  }, {})
}
